// In-editor combat playtest: a minimal HUD read straight off the sim's own
// `BreachPointSnapshot` - no state of its own beyond what the sim reports.
import { WEAPONS, type BreachPointSnapshot } from '@sstk/sunder-sim';

function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const PHASE_LABEL: Record<BreachPointSnapshot['phase'], string> = {
  menu: 'Menu',
  warmup: 'Warming up',
  buy: 'Buy phase',
  running: 'Live',
  paused: 'Paused',
  'round-end': 'Round over',
  'match-end': 'Match over',
};

export function PlaytestHud({
  snapshot,
  onExit,
}: {
  snapshot: BreachPointSnapshot | null;
  onExit: () => void;
}) {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', fontFamily: 'var(--ss-sans, sans-serif)', color: '#fff' }}>
      {/* Top bar: phase, timer, score. */}
      <div
        style={{
          position: 'absolute',
          top: 14,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          borderRadius: 999,
          background: 'rgba(10,10,14,0.6)',
          backdropFilter: 'blur(6px)',
          fontSize: 13,
          letterSpacing: '0.04em',
        }}
      >
        <span style={{ opacity: 0.75 }}>{snapshot ? PHASE_LABEL[snapshot.phase] : 'Loading...'}</span>
        {snapshot && (
          <>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatClock(snapshot.phase === 'warmup' ? snapshot.warmupTimeRemainingMs : snapshot.round.timeRemainingMs)}
            </span>
            <span style={{ color: TEAM_COLORS.raiders }}>{snapshot.teamCounts.raiders.alive}/{snapshot.teamCounts.raiders.total}</span>
            <span style={{ opacity: 0.5 }}>vs</span>
            <span style={{ color: TEAM_COLORS.wardens }}>{snapshot.teamCounts.wardens.alive}/{snapshot.teamCounts.wardens.total}</span>
            <span style={{ opacity: 0.6 }}>R{snapshot.round.number}</span>
          </>
        )}
      </div>

      {/* Kill feed, top right. */}
      {snapshot && snapshot.killFeed.length > 0 && (
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          {snapshot.killFeed.slice(-5).map((entry) => (
            <div
              key={entry.id}
              style={{
                background: 'rgba(10,10,14,0.6)',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ color: TEAM_COLORS[entry.killerTeam] }}>{entry.killerName}</span>
              {' '}{entry.headshot ? 'headshot' : 'killed'}{' '}
              <span style={{ color: TEAM_COLORS[entry.victimTeam] }}>{entry.victimName}</span>
              <span style={{ opacity: 0.6 }}> ({WEAPONS[entry.weaponId]?.name ?? entry.weaponId})</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom left: health + weapon. */}
      {snapshot && (
        <div style={{ position: 'absolute', bottom: 14, left: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {snapshot.playerAlive ? Math.round(snapshot.playerHealth) : 'Dead'}
            {snapshot.playerAlive && <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.6 }}> / {snapshot.playerMaxHealth} hp</span>}
          </div>
          <div style={{ fontSize: 14 }}>
            {WEAPONS[snapshot.playerActiveWeapon.id]?.name ?? snapshot.playerActiveWeapon.id}
            {snapshot.playerActiveWeapon.reloading
              ? ' - reloading'
              : ` - ${snapshot.playerActiveWeapon.ammoInClip}/${snapshot.playerActiveWeapon.ammoReserve}`}
          </div>
        </div>
      )}

      {/* Crosshair. */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', width: 6, height: 6, borderRadius: '50%', background: '#fff', transform: 'translate(-50%,-50%)', opacity: 0.85 }} />

      {/* Exit, always available. */}
      <button
        type="button"
        onClick={onExit}
        style={{
          position: 'absolute',
          bottom: 14,
          right: 14,
          pointerEvents: 'auto',
          background: 'rgba(10,10,14,0.7)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8,
          padding: '8px 14px',
          fontSize: 13,
          cursor: 'pointer',
        }}
        title="Return to editing (Esc)"
      >
        Exit playtest (Esc)
      </button>
    </div>
  );
}

const TEAM_COLORS: Record<string, string> = {
  raiders: '#e2703a',
  wardens: '#3aa0e2',
};
